import { create } from 'zustand';
import { type AuthStatus, type IdTokenClaims, type TokenSet } from './types';

export interface AuthState {
  status: AuthStatus;
  /** Access token kept in memory only (never persisted). */
  accessToken: string | null;
  claims: IdTokenClaims | null;
  /** Epoch milliseconds. */
  expiresAt: number | null;
  error: string | null;
  /** The user signed out (here or in another tab): the sign-in page must wait instead of redirecting. */
  signedOut: boolean;
  setStatus(s: AuthStatus): void;
  setTokens(t: TokenSet | null, claims: IdTokenClaims | null): void;
  setError(e: string | null): void;
  /** The session ended and could not be renewed; the current screen stays while the user signs in again. */
  expire(): void;
  clear(options?: { signedOut?: boolean }): void;
}

const initial = {
  status: 'idle' as AuthStatus,
  accessToken: null,
  claims: null,
  expiresAt: null,
  error: null,
  signedOut: false,
};

export const authStore = create<AuthState>()((set) => ({
  ...initial,
  setStatus: (status) => set({ status }),
  setTokens: (tokens, claims) =>
    set(
      tokens
        ? { status: 'authenticated', accessToken: tokens.accessToken, claims, expiresAt: tokens.expiresAt, error: null, signedOut: false }
        : { status: 'anonymous', accessToken: null, claims: null, expiresAt: null },
    ),
  setError: (error) => set(error ? { error, status: 'error' } : { error }),
  expire: () => set({ status: 'expired', accessToken: null, expiresAt: null }),
  clear: (options) => set({ ...initial, status: 'anonymous', signedOut: options?.signedOut ?? false }),
}));

/** Resets the store to its pristine state (tests). */
export function resetAuthStore(): void {
  authStore.setState({ ...initial });
}
